<script setup>
import { useAuthStore } from "@/stores";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { BellIcon, PlusIcon, XIcon } from "@heroicons/vue/outline";
import { MenuAlt2Icon } from "@heroicons/vue/solid";
import { computed, onBeforeMount } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppKintzButton from "./partials/AppKintzButton.vue";
import KintzReportMenus from "@/components/kintz/KintzReportMenus.vue";
import KintzResourceManager from "@/components/kintz/KintzResourceManager.vue";
import KintzAdminPortalMenus from "@/components/kintz/KintzAdminPortalMenus.vue";
import CreateUnitDialog from "@/components/unit/CreateUnitDialog.vue";
import { ref } from "vue";
import GlobalSearchDialog from "../GlobalSearchDialog.vue";
import TopNotification from "../header/TopNotification.vue";
import TopAppsMenu from "../header/TopAppsMenu.vue";
import { http } from "@/composable/httpService";
import AppKintzProfileMenu from "./partials/AppKintzProfileMenu.vue";
import AppNavbarSmallScreenMenu from "./partials/AppNavbarSmallScreenMenu.vue";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

let company_logo =
  "https://webinarinc-central.s3.us-west-1.amazonaws.com/okta-login/kintz-dist/kintznow.png";

const isSuperAdmin = computed(() => authStore.isSuperAdmin);
const isAccountManager = computed(() => authStore.isAccountManager);
const isDealerManager = computed(() => authStore.isDealerManager);
const isMultiStoreManager = computed(() => authStore.isMultiStoreManager);
const isLearner = computed(() => authStore.isLearner);

const isCreateUnitDialogOpen = ref(false);
const loadingApps = ref(false);
const apps = ref([]);

const selectedRouteName = computed(() => route.name);
const selectedRouteFullPath = computed(() => route.fullPath);

const navigation = ref({
  main: [
    { name: "Home", href: "/", current: true, route: "home" },
    {
      name: "Reports",
      href: "/reports",
      current: false,
      route: "reports",
    },
    {
      name: "Active Role Plays",
      href: "/active-role-plays",
      current: false,
      route: "active-role-plays",
    },
    {
      name: "Resource Manager",
      href: "/resource-manager",
      current: false,
      route: "filemanager",
    },
    {
      name: "News Feed",
      href: "/news-feed",
      current: false,
      route: "Announcement Home Page",
    },
  ],
});

const filteredNavigation = computed(() => {
  let menus = [...navigation.value.main];

  if (isLearner.value) {
    menus = menus.filter((menu) => menu.name !== "Active Role Plays");
  }

  if (
    isSuperAdmin.value ||
    isAccountManager.value ||
    isDealerManager.value ||
    isMultiStoreManager.value
  ) {
    menus = [
      ...menus,
      {
        name: "Admin Portal",
        href: "/central/manage-users",
        current: false,
        route: "#",
      },
    ];
  }

  return menus;
});

const handleGoToRoute = (routePath, fullPath = false) => {
  if (fullPath) {
    router.push(routePath);
    return;
  }
  router.push({ name: routePath });
};

const handleAdminPortalNavigation = (item, event) => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  if (item.urlType === 'lms') {
    // For LMS routes, use Vue router
    handleGoToRoute(item.to, true);
  } else {
    // For central URLs, navigate directly using window.location
    // This works reliably on mobile devices
    window.location.href = item.to;
  }
};

const setCreateUnitDialog = (isOpen) => {
  isCreateUnitDialogOpen.value = isOpen;
};

const handleFetchCentralApps = () => {
  loadingApps.value = true;
  http()
    .get("/central/get-app-menu")
    .then(({ data }) => {
      apps.value = data;
    })
    .finally(() => (loadingApps.value = false));
};

onBeforeMount(() => {
  handleFetchCentralApps();
});
</script>
<template>
  <Disclosure
    as="nav"
    class="border-b border-kintz-primary-1 bg-kintz-primary-1 sticky top-0 z-50"
    v-slot="{ open }"
  >
    <div class="mx-auto max-w-[2000px] px-4 sm:px-6 lg:px-8">
      <div class="flex h-16 justify-between">
        <div class="flex">
          <div class="flex shrink-0 items-center">
            <img
              class="block w-auto h-10 md:h-auto md:w-28 lg:w-32 lg:hidden"
              :src="
                company_logo ||
                'https://webinarinc-central.s3.us-west-1.amazonaws.com/okta-login/kintz-dist/kintznow.png'
              "
              alt="Your Company"
            />
            <img
              class="hidden w-auto h-10 md:h-auto md:w-28 lg:w-32 lg:block"
              :src="
                company_logo ||
                'https://webinarinc-central.s3.us-west-1.amazonaws.com/okta-login/kintz-dist/kintznow.png'
              "
              alt="Your Company"
            />
          </div>
          <div class="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-6">
            <template v-for="(item, index) in filteredNavigation" :key="index">
              <RouterLink
                :to="item.href"
                v-if="
                  item.name === 'Home' ||
                  item.name === 'Active Role Plays' ||
                  item.name === 'My Comments' ||
                  item.name === 'News Feed'
                "
                :class="[
                  selectedRouteName === item.route
                    ? 'border-kintz-primary-2 text-kintz-primary-2'
                    : 'border-transparent text-kintz-primary-4 hover:border-kintz-primary-2 hover:text-kintz-primary-2',
                  'inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium',
                  {
                    'hidden xl:flex': item.name === 'News Feed',
                    'hidden lg:flex': item.name === 'Active Role Plays',
                  },
                ]"
                :aria-current="item.current ? 'page' : undefined"
                >{{ item.name }}</RouterLink
              >

              <KintzReportMenus
                v-else-if="item.name === 'Reports'"
                class="hidden md:block"
              />
              <KintzAdminPortalMenus
                class="hidden 2xl:block"
                v-has-restrict-learner
                v-else-if="item.name === 'Admin Portal'"
              />
              <KintzResourceManager
                class="hidden xl:block"
                v-else-if="item.name === 'Resource Manager'"
              />
            </template>
            <a
              href="https://kintzgroup.com/shop/"
              target="_blank"
              class="border-transparent text-kintz-primary-4 hover:border-kintz-primary-2 hover:text-kintz-primary-2 items-center border-b-2 px-1 pt-1 text-sm font-medium hidden 2xl:inline-flex"
            >
              Shop
            </a>
            <a
              href="https://kintzgroup.com/product-category/workshops/"
              target="_blank"
              class="border-transparent text-kintz-primary-4 hover:border-kintz-primary-2 hover:text-kintz-primary-2 items-center border-b-2 px-1 pt-1 text-sm font-medium hidden 2xl:inline-flex"
            >
              Workshops
            </a>

            <AppNavbarSmallScreenMenu class="2xl:hidden" />
            <!--  -->
          </div>
        </div>
        <div class="hidden sm:ml-6 sm:flex sm:items-center gap-2">
          <AppKintzButton
            v-if="isSuperAdmin"
            type="primary-2"
            size="lg"
            @click="setCreateUnitDialog(true)"
          >
            <PlusIcon class="size-5" aria-hidden="true" />
            Upload Content
          </AppKintzButton>
          <div class="ml-2 flex-shrink-0 flex justify-center items-center">
            <GlobalSearchDialog />
          </div>
          <button
            type="button"
            v-if="false"
            class="relative p-1 text-kintz-primary-4 hover:text-kintz-primary-2 focus:ring-2 focus:ring-kintz-primary-2 focus:ring-offset-2 focus:outline-hidden"
          >
            <span class="absolute -inset-1.5" />
            <span class="sr-only">View notifications</span>
            <BellIcon class="size-6" aria-hidden="true" />
          </button>

          <TopNotification />
          <TopAppsMenu v-if="!loadingApps" :apps="apps" kintz-app />

          <!-- Profile dropdown -->
          <AppKintzProfileMenu />
        </div>
        <div class="-mr-2 flex items-center gap-2 sm:hidden">
          <TopNotification />
          <TopAppsMenu v-if="!loadingApps" :apps="apps" kintz-app />
          <AppKintzProfileMenu />
          <!-- Mobile menu button -->
          <DisclosureButton
            class="relative inline-flex items-center justify-center rounded-md bg-kintz-primary-1 p-2 text-kintz-primary-2 hover:bg-kintz-primary-3 focus:ring-2 focus:ring-kintz-primary-2 focus:ring-offset-2 focus:outline-hidden"
          >
            <span class="absolute -inset-0.5" />
            <span class="sr-only">Open main menu</span>
            <MenuAlt2Icon
              v-if="!open"
              class="block size-6"
              aria-hidden="true"
            />
            <XIcon v-else class="block size-6" aria-hidden="true" />
          </DisclosureButton>
        </div>
      </div>
    </div>

    <DisclosurePanel class="sm:hidden">
      <div class="space-y-1 pt-2 pb-3">
        <template v-for="(item, index) in filteredNavigation" :key="index">
          <DisclosureButton
            v-if="
              item.name === 'Home' ||
              item.name === 'Active Role Plays' ||
              item.name === 'My Comments' ||
              item.name === 'News Feed' ||
              item.name === 'Reports' ||
              item.name === 'Resource Manager'
            "
            as="button"
            @click="handleGoToRoute(item.route)"
            :class="[
              selectedRouteName === item.route
                ? 'border-kintz-primary-2 text-kintz-primary-2'
                : 'border-transparent text-kintz-primary-4 hover:border-kintz-primary-2 hover:text-kintz-primary-2',
              'block border-l-4 py-2 pr-4 pl-3 text-base font-medium',
            ]"
            :aria-current="item.current ? 'page' : undefined"
          >
            {{ item.name }}
          </DisclosureButton>

          <KintzAdminPortalMenus
            class="mr-3"
            v-has-restrict-learner
            v-else-if="item.name === 'Admin Portal'"
          >
            <template #default="{ filteredRoutes }">
              <div
                class="flex justify-start px-4 items-center text-kintz-primary-3 uppercase font-medium text-sm"
              >
                <h1>Admin Portal</h1>
              </div>
              <template v-for="(item, index) in filteredRoutes">
                <DisclosureButton
                  v-if="item.urlType === 'lms'"
                  :key="`lms-${index}`"
                  as="button"
                  @click="handleAdminPortalNavigation(item, $event)"
                  :class="[
                    selectedRouteFullPath === item.to
                      ? 'border-kintz-primary-2 text-kintz-primary-2'
                      : 'border-transparent text-kintz-primary-4 hover:border-kintz-primary-2 hover:text-kintz-primary-2',
                    'block border-l-4 py-2 pr-4 pl-3 text-base font-medium w-full text-left',
                  ]"
                  :aria-current="item.current ? 'page' : undefined"
                >
                  {{ item.name }}
                </DisclosureButton>
                <a
                  v-else
                  :key="`central-${index}`"
                  :href="item.to"
                  @click="(e) => { e.stopPropagation(); }"
                  :class="[
                    selectedRouteFullPath === item.to
                      ? 'border-kintz-primary-2 text-kintz-primary-2'
                      : 'border-transparent text-kintz-primary-4 hover:border-kintz-primary-2 hover:text-kintz-primary-2',
                    'block border-l-4 py-2 pr-4 pl-3 text-base font-medium w-full text-left',
                  ]"
                >
                  {{ item.name }}
                </a>
              </template>
            </template>
          </KintzAdminPortalMenus>
        </template>
      </div>
    </DisclosurePanel>
    <CreateUnitDialog
      :isCreateUnitDialogOpen="isCreateUnitDialogOpen"
      @closeCreateUnitDialog="setCreateUnitDialog(false)"
    />
  </Disclosure>
</template>

<style lang="scss" scoped></style>
